import React, { useState, useEffect } from 'react';

const SHARED_FIELDS_OPTIONS = [
  { value: 'income data', label: 'Income & salary data' },
  { value: 'legal context', label: 'Legal case context' },
  { value: 'career data', label: 'Career history' },
  { value: 'tax documents', label: 'Tax documents' },
  { value: 'bank statements', label: 'Bank statements' },
];

const RETENTION_OPTIONS = [
  { value: '3m', label: '3 months', months: 3 },
  { value: '6m', label: '6 months', months: 6 },
  { value: '12m', label: '12 months', months: 12 },
];

function addMonths(months) {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString();
}

const ConnectFlow = ({ user, navigateTo, goBack, activeProfileType }) => {
  const [pro, setPro] = useState(null);
  const [domain, setDomain] = useState('FINANCE');
  const [profileType, setProfileType] = useState('PERSONAL');

  const [purpose, setPurpose] = useState('');
  const [sharedFields, setSharedFields] = useState([]);
  const [retention, setRetention] = useState('3m');
  const [summarySnapshot, setSummarySnapshot] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('axigon_selected_pro');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.pro) setPro(parsed.pro);
        if (parsed.domain) setDomain(parsed.domain);
        if (parsed.profileType) setProfileType(parsed.profileType);
      }
    } catch {}

    // Fallback: use activeProfileType prop
    const pt = activeProfileType || (user?.accountType === 'personal' ? 'PERSONAL' : 'BUSINESS');
    setProfileType(pt);
  }, [activeProfileType, user]);

  const toggleField = (val) => {
    setSharedFields(prev =>
      prev.includes(val) ? prev.filter(f => f !== val) : [...prev, val]
    );
  };

  const retentionMonths = RETENTION_OPTIONS.find(r => r.value === retention)?.months || 3;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!purpose.trim()) { setError('Please describe the purpose of your consultation.'); return; }
    if (sharedFields.length === 0) { setError('Please select at least one type of data to share.'); return; }

    setSubmitting(true);
    try {
      const res = await fetch('/api/profiles?action=createMatter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          profileType,
          domain,
          summarySnapshot: summarySnapshot || purpose,
          consent: {
            purpose,
            sharedFields,
            retentionUntil: addMonths(retentionMonths),
            professionalScope: domain,
          },
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to submit');

      // If a professional was selected, assign them
      if (pro?.professionalId && data.matter?.matterId) {
        await fetch('/api/profiles?action=assignMatter', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            matterId: data.matter.matterId,
            profileType,
            professionalId: pro.professionalId,
          }),
        });
      }

      sessionStorage.removeItem('axigon_selected_pro');
      setSuccess(data.matter);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg)', paddingTop: '80px' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '60px 20px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>✓</div>
          <h2 style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '12px', letterSpacing: '-0.02em' }}>
            Request submitted
          </h2>
          <p style={{ fontSize: '16px', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: '8px' }}>
            Your matter has been submitted{pro ? ` to ${pro.name}` : ''}.
          </p>
          <p style={{ fontSize: '14px', color: 'var(--text-tertiary)', marginBottom: '32px' }}>
            Status: <strong>{success.status}</strong>
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button
              onClick={() => navigateTo('matter-status')}
              style={{ padding: '12px 28px', borderRadius: 'var(--radius-control)', backgroundColor: 'var(--accent)', color: 'var(--bg)', border: 'none', fontSize: '15px', fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              View my matters
            </button>
            <button
              onClick={() => goBack()}
              style={{ padding: '12px 28px', borderRadius: 'var(--radius-control)', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-primary)', border: '1px solid var(--border)', fontSize: '15px', fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg)', paddingTop: '80px' }}>
      <div style={{ maxWidth: '640px', margin: '0 auto', padding: '40px 20px' }}>

        {/* Header */}
        <button
          onClick={() => goBack()}
          style={{ background: 'none', border: 'none', color: 'var(--accent)', fontSize: '14px', cursor: 'pointer', padding: 0, marginBottom: '24px', fontFamily: 'inherit' }}
        >
          ← Back
        </button>

        {pro && (
          <div style={{ padding: '16px 20px', borderRadius: 'var(--radius-control)', backgroundColor: 'var(--bg-secondary)', border: '1px solid var(--border)', marginBottom: '28px' }}>
            <p style={{ margin: 0, fontSize: '13px', color: 'var(--text-tertiary)' }}>Connecting with</p>
            <p style={{ margin: '4px 0 0', fontSize: '17px', fontWeight: 600, color: 'var(--text-primary)' }}>
              {pro.name} <span style={{ fontSize: '12px', color: 'var(--accent)', fontWeight: 500 }}>{pro.credential}</span>
            </p>
          </div>
        )}

        <h1 style={{ fontSize: '28px', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px', letterSpacing: '-0.02em' }}>
          Share your context
        </h1>
        <p style={{ fontSize: '15px', color: 'var(--text-secondary)', marginBottom: '32px', lineHeight: 1.6 }}>
          Before connecting, tell us what you need help with and confirm what data you consent to share.
        </p>

        <form onSubmit={handleSubmit}>

          {/* Summary snapshot */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
              What will be shared with the professional
            </label>
            <textarea
              value={summarySnapshot}
              onChange={e => setSummarySnapshot(e.target.value)}
              placeholder="e.g. I need help with tax planning for FY 2024-25. I have salary income and capital gains."
              rows={3}
              style={{ width: '100%', padding: '12px 14px', borderRadius: 'var(--radius-control)', border: '1.5px solid var(--border)', fontSize: '14px', color: 'var(--text-primary)', backgroundColor: 'var(--bg)', resize: 'vertical', fontFamily: 'inherit', outline: 'none', lineHeight: 1.6 }}
            />
          </div>

          {/* Purpose (consent) */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
              Purpose of consultation <span style={{ color: 'var(--accent)' }}>*</span>
            </label>
            <input
              type="text"
              value={purpose}
              onChange={e => setPurpose(e.target.value)}
              placeholder="e.g. Tax planning for FY 2024-25"
              required
              style={{ width: '100%', padding: '12px 14px', borderRadius: 'var(--radius-control)', border: '1.5px solid var(--border)', fontSize: '14px', color: 'var(--text-primary)', backgroundColor: 'var(--bg)', fontFamily: 'inherit', outline: 'none' }}
            />
          </div>

          {/* Shared fields (consent) */}
          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '12px' }}>
              Data you consent to share <span style={{ color: 'var(--accent)' }}>*</span>
            </label>
            <div style={{ display: 'grid', gap: '8px' }}>
              {SHARED_FIELDS_OPTIONS.map(opt => (
                <label
                  key={opt.value}
                  style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', padding: '10px 14px', borderRadius: 'var(--radius-control)', border: `1.5px solid ${sharedFields.includes(opt.value) ? 'var(--accent)' : 'var(--border)'}`, backgroundColor: sharedFields.includes(opt.value) ? 'rgba(0,194,124,0.05)' : 'var(--bg)' }}
                >
                  <input
                    type="checkbox"
                    checked={sharedFields.includes(opt.value)}
                    onChange={() => toggleField(opt.value)}
                    style={{ accentColor: 'var(--accent)', width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <span style={{ fontSize: '14px', color: 'var(--text-primary)' }}>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Retention period (consent) */}
          <div style={{ marginBottom: '32px' }}>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '8px' }}>
              Data retention period <span style={{ color: 'var(--accent)' }}>*</span>
            </label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {RETENTION_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setRetention(opt.value)}
                  style={{ padding: '8px 18px', borderRadius: 'var(--radius-pill)', border: `1.5px solid ${retention === opt.value ? 'var(--accent)' : 'var(--border)'}`, backgroundColor: retention === opt.value ? 'var(--accent)' : 'var(--bg)', color: retention === opt.value ? 'var(--bg)' : 'var(--text-secondary)', fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div style={{ padding: '12px 16px', borderRadius: 'var(--radius-control)', backgroundColor: '#fff2f2', border: '1px solid #ffd6d6', color: '#c00', fontSize: '14px', marginBottom: '16px' }}>
              {error}
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            style={{ width: '100%', padding: '14px', borderRadius: 'var(--radius-control)', border: 'none', backgroundColor: 'var(--accent)', color: 'var(--bg)', fontSize: '16px', fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.7 : 1, fontFamily: 'inherit', transition: 'opacity 0.15s' }}
          >
            {submitting ? 'Submitting…' : 'Confirm and connect'}
          </button>
          <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '12px', lineHeight: 1.5, textAlign: 'center' }}>
            By submitting, you consent to sharing the selected data with the professional for {retentionMonths} months.
          </p>
        </form>
      </div>
    </div>
  );
};

export default ConnectFlow;
